<template>
    <popup-modal-confirm
        custom-classes="w-684"
        :confirm-text="application ? $tc('applications.move') : $tc('applications.move_multiple', bulkCount, { count: bulkCount })"
        :disabled="!canSubmit"
        :has-close-button="true"
        :title="$t('applications.move_application', {
            subject: application ? application.fullName : $t('applications.selected_applications')
        })"
        :working="form.submitting"
        confirm-button="success"
        @close="$emit('close')"
        @confirm="move"
    >
        <loading v-if="!application && !bulkKeyData"/>
        <div v-else>
            <select-dropdown
                static-id="moveToJobDropdown"
                v-model="form.job"
                :autoTranslate="false"
                :options="jobOptions"
                class="mb-16"
                searchable
                @change="handleJobSelection"
            />

            <select-dropdown
                v-if="form.job != 0"
                v-model="form.stage"
                :autoTranslate="false"
                class="mb-16"
                :options="stageOptions"
                no-auto-sort
            />
        </div>
    </popup-modal-confirm>
</template>

<script>
import Application from '../../models/Application';
import Loading from './Loading.vue';
import SelectDropdown from '../forms/SelectDropdown.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useJobsStore} from '../../state/modules/jobs';
import Form from '../../etc/forms/Form';
import Job from '../../models/Job';
import * as applications from '../../services/ApplicationService';
import {normalizeJson} from '../../../shared/helpers';
import * as bulkApplicationKey from '../../services/BulkApplicationKeyService';
import PopupModalConfirm from './PopupModalConfirm';
import {ADD_NOTIFICATION} from "../../state/mutation-types";

export default {
    components: {
        'popup-modal-confirm': PopupModalConfirm,
        'loading': Loading,
        'select-dropdown': SelectDropdown,
    },
    props: {
        application: Application,
        requestFilters: Object,
        applications: Array,
        stages: Array,
        defaultJob: {
            type: Job,
            required: false,
            default: null,
        },
    },
    data: function () {
        return {
            selectedJob: this.defaultJob,
            form: new Form([
                {name: 'job', value: this.defaultJob ? this.defaultJob.id : 0},
                {name: 'stage', value: 0},
            ]),
            bulkKeyData: null,
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        availableJobs() {

            let query = useRepo(Job)
                .with('stages')
                .where('company_id', this.currentCompanyId)
                .where('archived', null);

            if (this.application && this.application.stage) {
                query = query.where(job => job.id !== this.application.stage.job_id);
            }

            return query.get().sort((a, b) => a.sequence > b.sequence ? 1 : -1);
        },
        jobOptions() {

            let jobs = this.availableJobs.map(job => ({
                value: job.id,
                label: this.translated(job.title, this.locale, true) + (job.active_location.city ? ' - ' + job.active_location.city : ''),
            }));

            return [
                {value: 0, label: '--- ' + this.$t('applications.choose_job') + ' ---'},
                ...jobs,
            ];
        },
        stageOptions() {

            if (!this.selectedJob) {
                return {};
            }

            let stages = this.selectedJob.stages
                .sort((a, b) => a.order - b.order)
                .map(stage => ({value: stage.id, label: stage.name}));

            return [
                {value: 0, label: '--- ' + this.$t('applications.choose_stage') + ' ---'},
                ...stages,
            ];
        },
        canSubmit() {
            return parseInt(this.form.job) && parseInt(this.form.stage);
        },
        bulkCount() {
            if (!this.bulkKeyData) {
                return 0;
            }

            return this.bulkKeyData.attributes.applications_count;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useJobsStore, ['fetch']),
        handleJobSelection(selected) {
            this.selectedJob = this.availableJobs.find(job => job.id === selected);
            this.form.stage = 0;
        },
        move() {

            this[this.application ? 'moveOneApplication' : 'moveApplications']()
                .then(() => this.$emit('close'));
        },
        moveOneApplication() {
            return this.form.submit(data => applications.update(this.application.id, {
                data: {
                    relationships: {
                        stage: {
                            data: {
                                type: 'stages',
                                id: data.stage,
                            },
                        },
                    },
                },
            }, true)).then(result => useRepo(Application).save(normalizeJson(result)));
        },
        moveApplications() {
            return this.form.submit(data => applications.massUpdate(this.requestFilters, {
                data: {
                    relationships: {
                        stage: {
                            data: {
                                type: 'stages',
                                id: data.stage,
                            },
                        },
                    },
                    bulkApplicationId: this.bulkKeyData.id,
                },
            })).then(result => {
                if (result?.data?.message === 'started') {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('bulkaction.application.massUpdate.started'),
                    });
                    this.$emit('close');
                    return;
                }
                return useRepo(Application).save(normalizeJson(result.data));
            });
        },
        getBulkKey() {
            this.form.submit(data => bulkApplicationKey.get({
                    data: {
                        selection: {
                            applications: this.applications,
                            stages: this.stages,
                        },
                        relationships: {
                            stage: {
                                data: {
                                    type: 'stages',
                                    id: data.stage,
                                },
                            },
                        },
                    },
                }, this.requestFilters),
            ).then(data => this.bulkKeyData = data);
        },
    },
    mounted() {
        this.fetch({activeState: 'active', all: 1});

        if (!this.application) {
            this.getBulkKey();
        }
    }
}
</script>
